import { useEffect, useRef, useState } from "react";
import { SliderCaptcha } from "../components/SliderCaptcha";
import { Link, useNavigate, Navigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Header from "../components/azhome/AzHeader";
import { useAuth } from "../context/AuthContext";
import SocialAuth from "../components/SocialAuth";
import "../auth.css";

const Register = () => {
  const { register, resendVerification, user, loading } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [refCode, setRefCode] = useState((searchParams.get("ref") || "").toUpperCase());
  const [error, setError] = useState("");
  const [captchaToken, setCaptchaToken] = useState("");
  const [capKey, setCapKey] = useState(0);
  const [honey, setHoney] = useState("");
  const openedAt = useRef(Date.now());
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [resent, setResent] = useState(false);
  const [emailError, setEmailError] = useState("");

  // Instant suspicious/disposable email rejection as the user types (server enforces too).
  useEffect(() => {
    setEmailError("");
    const v = email.trim().toLowerCase();
    if (!v.includes("@")) return undefined;
    let stale = false;  // a slow reply for an earlier, half-typed address must not show up under the current one
    const id = setTimeout(async () => {
      try {
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/check-email?email=${encodeURIComponent(v)}`);
        const d = await r.json();
        if (!stale && d && d.allowed === false) {
          setEmailError(d.reason === "disposable"
            ? "This email provider isn't allowed. Please use a permanent email address (e.g. Gmail, Outlook)."
            : "Please enter a valid email address.");
        }
      } catch { /* network hiccup: the server-side check still applies on submit */ }
    }, 700);
    return () => { stale = true; clearTimeout(id); };
  }, [email]);

  if (!loading && user) {
    return <Navigate to="/dashboard" replace />;
  }

  const onResend = async () => {
    try { await resendVerification(email); setResent(true); } catch { setResent(true); }
  };

  if (submitted) {
    return (
      <>
        <Header />
        <main className="auth-page">
          <div className="auth-card" data-testid="register-verify-sent">
            <p className="overline">{t("auth.getStarted")}</p>
            <h1>{t("auth.checkEmailTitle")}</h1>
            <p className="auth-sub">{t("auth.checkEmailBody", { email })}</p>
            <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 8 }}>
              <button className="auth-submit" type="button" onClick={() => navigate("/login")} data-testid="verify-goto-login">{t("auth.loginLink")}</button>
              <button className="auth-ghost-btn" type="button" onClick={onResend} data-testid="verify-resend-btn"
                style={{ background: "none", border: "1px solid var(--line,#e2e8f0)", borderRadius: 10, padding: "12px", cursor: "pointer", fontWeight: 600 }}>
                {resent ? t("auth.verifyResent") : t("auth.resendVerification")}
              </button>
            </div>
          </div>
        </main>
      </>
    );
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (emailError) return;
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (!captchaToken) {
      setError("Please slide the puzzle piece into the gap to prove you're human.");
      return;
    }
    setBusy(true);
    try {
      const res = await register(fullName, email, password, refCode.trim() || undefined,
        { captcha_token: captchaToken, website: honey || undefined, form_ms: Date.now() - openedAt.current });
      if (res?.requires_verification) {
        setSubmitted(true);   // show the "check your email" screen
      } else {
        navigate("/dashboard");
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === "string") {
        setError(detail);
        setCaptchaToken(""); setCapKey((k) => k + 1);  // token is single-use: fresh puzzle
      } else if (Array.isArray(detail) && detail.length) {
        setError((detail[0].msg || "Please check your details.").replace(/^Value error, /, ""));
        setCaptchaToken(""); setCapKey((k) => k + 1);
      } else {
        setError("Registration failed. Please try again.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Header />
      <main className="auth-page">
        <div className="auth-card">
          <p className="overline">{t("auth.getStarted")}</p>
          <h1>{t("auth.registerTitle")}</h1>
          <p className="auth-sub">{t("auth.registerSub")}</p>
          {error && <div className="auth-error">{error}</div>}
          <form onSubmit={onSubmit}>
            <div className="auth-field">
              <label htmlFor="fullName">{t("auth.fullName")}</label>
              <input
                id="fullName"
                type="text"
                required
                autoComplete="name"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Your full name"
              />
            </div>
            <div className="auth-field">
              <label htmlFor="reg-email">{t("auth.emailLabel")}</label>
              <input
                id="reg-email"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                style={emailError ? { borderColor: "#e5484d" } : undefined}
              />
              {emailError && <div className="auth-error" style={{ marginTop: 6 }} data-testid="register-email-error">{emailError}</div>}
            </div>
            <div className="auth-field">
              <label htmlFor="reg-password">{t("auth.passwordLabel")}</label>
              <input
                id="reg-password"
                type="password"
                required
                minLength={6}
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t("security.newPasswordPh")}
              />
            </div>
            <div className="auth-field">
              <label htmlFor="reg-ref">{t("auth.referralCode")} <span style={{ opacity: 0.6, fontWeight: 400 }}>({t("auth.optional")})</span></label>
              <input
                id="reg-ref"
                type="text"
                data-testid="register-refcode"
                autoComplete="off"
                value={refCode}
                onChange={(e) => setRefCode(e.target.value.toUpperCase())}
                placeholder="IMP••••••"
              />
            </div>
            <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" value={honey}
              onChange={(e) => setHoney(e.target.value)} className="hp-field" data-testid="register-hp" />
            <SliderCaptcha key={capKey} onVerified={setCaptchaToken} />
            <button className="button blue auth-submit" type="submit" disabled={busy || !captchaToken || !!emailError} data-testid="register-submit">
              {busy ? t("auth.creating") : t("auth.createBtn")}
            </button>
          </form>
          <SocialAuth label={t("auth.orSignUpWith") || "or sign up with"} />
          <p className="auth-switch">
            {t("auth.alreadyHave")} <Link to="/login">{t("auth.loginLink")}</Link>
          </p>
        </div>
      </main>
    </>
  );
};

export default Register;
